import { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import useHttpHook from "../../../Hooks/HttpHook";
import { UserEventById } from "../../../utils/ApiConstant";
import { getEventImageUrl } from "../../../utils/Helper";
import { updEventData } from "../../../ReduxSlice/Event/eventSlice";
import { EventLanguage } from "../../../utils/Languages";
import MainComponentWrapper from "../../Common/MainComponentWrapper";
import GalleryModal from "../../Common/Modal/GalleryModal";
import ImageGallery from "../../UiElement/ImageGallery";
import EventLocation from "./EventLocation";
import { showErrorToast } from "../../../utils/notify";

const EventDetails = ({ sharingModal, setSharingModal, publicAccess }) => {
  const [loading, sendRequest, error] = useHttpHook();
  const { eventId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [publicLoadState, setPublicLoadState] = useState("loading");
  const dispatch = useDispatch();
  const EventData = useSelector((state) => state.event.data);
  const EventStatus = useSelector((state) => state.event.status);
  const query = new URLSearchParams(window.location.search);
  const mainImagePath = EventData?.mainimage;
  const posterFallback = "/event/assets/img/sellingevents.png";
  const [posterSrc, setPosterSrc] = useState(posterFallback);

  useEffect(() => {
    setPosterSrc(getEventImageUrl(mainImagePath, posterFallback));
  }, [mainImagePath, eventId]);

  useEffect(() => {
    getEventData();
  }, [eventId]);

  useEffect(() => {
    if (window.location.search) {
      if (query?.get("referralId")) {
        localStorage.setItem("referralId", query.get("referralId"));
      } else {
        localStorage.removeItem("referralId");
      }
    }
  }, [query]);

  const getEventData = async () => {
    if (publicAccess) {
      setPublicLoadState("loading");
    }
    const event = await sendRequest(UserEventById + eventId, { method: "get" });
    if (event.status === 1) {
      dispatch(updEventData(event));
      if (publicAccess) {
        setPublicLoadState("ready");
      }
      return;
    }
    if (publicAccess) {
      setPublicLoadState("error");
    }
  };

  // Extract and validate a video URL from stored HTML or plain value.
  // Returns a string URL when valid (allowlist + https), otherwise null.
  const getValidVideoUrl = (htmlOrValue) => {
    if (!htmlOrValue || typeof htmlOrValue !== "string") return null;

    try {
      // Try to extract src from an iframe tag if present.
      const iframeMatch = htmlOrValue.match(/<iframe[^>]+src=["']([^"'>]+)["']/i);
      const src = iframeMatch ? iframeMatch[1] : null;

      // If iframe not present, check if the value itself is a URL.
      const candidate = src || htmlOrValue.trim();

      let url;
      try {
        url = new URL(candidate);
      } catch (err) {
        return null;
      }

      // Allow only https and known video hosts.
      const allowedHosts = new Set([
        "www.youtube.com",
        "youtube.com",
        "youtu.be",
        "player.vimeo.com",
        "vimeo.com",
      ]);

      if (url.protocol !== "https:" || !allowedHosts.has(url.hostname)) {
        return null;
      }

      // Normalize youtu.be to youtube watch/embed link
      if (url.hostname === "youtu.be") {
        const id = url.pathname.replace("/", "");
        return `https://www.youtube.com/watch?v=${id}`;
      }

      return url.href;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    if (error) {
      if (toast.isActive(error) === false) {
        showErrorToast(error);
        if (error === "You are unAuthrised" && !publicAccess) {
          window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
        }
      }
    }
  }, [error]);

  return (
    <MainComponentWrapper Loading={loading} errorInfo={error}>
      {publicAccess && publicLoadState === "error" && (
        <div className="event-details-not-found">
          <i className="bi bi-calendar-x" aria-hidden="true"></i>
          <p>This event could not be found.</p>
        </div>
      )}
      {EventStatus && (!publicAccess || publicLoadState === "ready") && (
        <div className="event-details-body">
          <div className="row g-4 g-xl-5 event-details-layout">
            <div className="col-lg-7">
              <article className="event-details-main" aria-label="Event information">
                <div className="event-details-hero">
                  {posterSrc && posterSrc !== posterFallback && (
                    <img
                      className="event-details-hero-bg"
                      src={posterSrc}
                      alt=""
                      aria-hidden="true"
                      onError={() => setPosterSrc(posterFallback)}
                    />
                  )}
                  <div className="event-details-hero-stage">
                    <button
                      type="button"
                      className="event-details-poster-btn"
                      aria-label="View full size poster"
                      onClick={() => setIsOpen(true)}
                    >
                      <img
                        className="event-details-poster"
                        src={posterSrc || posterFallback}
                        alt={EventData?.title || "Event poster"}
                        onError={() => setPosterSrc(posterFallback)}
                      />
                    </button>
                    {EventData?.mainimage && (
                      <GalleryModal
                        filesData={[EventData.mainimage]}
                        Open={isOpen}
                        Index="0"
                        onClose={() => setIsOpen(false)}
                      />
                    )}
                  </div>
                </div>

                {EventData?.description && (
                  <section
                    className="event-details-block"
                    aria-label="About this event"
                  >
                    <div className="event-details-block-head">
                      <h2 className="event-details-block-title">
                        About This Event
                      </h2>
                      <p className="event-details-block-sub">
                        Everything you need to know before you go.
                      </p>
                    </div>
                    <div
                      className="event-details-description"
                      dangerouslySetInnerHTML={{
                        __html: EventData.description,
                      }}
                    />
                  </section>
                )}

                {EventData?.gallery?.length > 0 && (
                  <ImageGallery
                    variant="event-details"
                    title={EventLanguage.UserEvents.detail.Gallery}
                    filesData={[EventData.gallery]}
                  />
                )}

                {EventData?.video && (
                  <section
                    className="event-details-block mb-3"
                    aria-label="Event video"
                  >
                    <div className="event-details-block-head">
                      <h2 className="event-details-block-title">Video</h2>
                    </div>
                    <div className="event-details-video ">
                      {(() => {
                        const validUrl = getValidVideoUrl(EventData.video);
                        if (validUrl) {
                          return (
                            <a
                              className="event-video-play-link "
                              href={validUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Play video"
                            >
                              {validUrl}
                            </a>
                          );
                        }

                        // No output when link is invalid.
                        return EventData.video;
                      })()}
                    </div>
                  </section>
                )}
              </article>
            </div>

            <div className="col-lg-5">
              <EventLocation
                publicAccess={publicAccess}
                setSharingModal={setSharingModal}
                sharingModal={sharingModal}
                Id={EventData?._id}
                organizer={EventData?.createdBy}
                title={EventData?.title}
                tags={EventData?.tag}
                eventData={EventData}
                eventTiming={EventData?.eventTiming}
                location={EventData?.location}
                isCancelled={EventData?.isCancelled}
              />
            </div>
          </div>
        </div>
      )}
    </MainComponentWrapper>
  );
};

export default memo(EventDetails);
